import { useState, useEffect } from "react";
import { X, Gift, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";

export function ReferralDiscountBanner() {
  const [visible, setVisible] = useState(false);
  const [discountInfo, setDiscountInfo] = useState<{
    monthsRemaining: number;
    monthlySavings: number;
    totalSaved: number;
    nextBillingAmount: number;
  } | null>(null);
  const navigate = useNavigate();
  const { usage, loading } = useSubscription();

  useEffect(() => {
    if (!loading && usage) {
      loadDiscountInfo();
    }
  }, [loading, usage]);

  const loadDiscountInfo = () => {
    try {
      // Get referral discount info from subscription context
      // The usage data from SubscriptionContext already contains all necessary info from backend
      const hasReferralDiscount = usage?.referral_stripe_coupon_id;

      if (!hasReferralDiscount) {
        setVisible(false);
        return; // No active referral discount
      }

      // Check if banner was dismissed recently (within last 24 hours)
      const dismissedAt = localStorage.getItem('referral_banner_dismissed');
      if (dismissedAt) {
        const dismissedTime = parseInt(dismissedAt);
        const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
        if (hoursSinceDismissed < 24) {
          return; // Don't show if dismissed within last 24 hours
        }
      }

      // Get discount details from backend
      const monthsRemaining = usage?.referral_months_remaining ?? 0;
      const monthlySavings = usage?.referral_monthly_savings ?? 33;
      const totalSaved = usage?.referral_total_saved ?? 0;

      // Only show if there are months remaining
      if (monthsRemaining > 0) {
        // Calculate next billing amount (base plan $99 - discount $33)
        const basePlanPrice = 99;
        const nextBillingAmount = basePlanPrice - monthlySavings;

        setDiscountInfo({
          monthsRemaining,
          monthlySavings,
          totalSaved,
          nextBillingAmount
        });
        setVisible(true);

        console.log('[ReferralDiscountBanner] Showing referral discount:', {
          monthsRemaining,
          monthlySavings,
          totalSaved,
          nextBillingAmount
        });
      }

    } catch (error) {
      console.error('Error loading discount info:', error);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    // Store dismissal in localStorage (show again on page reload)
    localStorage.setItem('referral_banner_dismissed', Date.now().toString());
  };

  if (!visible || !discountInfo) return null;

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Gift className="h-5 w-5 text-green-600" />
              </div>
            </div>

            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">
                You're saving ${discountInfo.monthlySavings}/month for {discountInfo.monthsRemaining} more month{discountInfo.monthsRemaining !== 1 ? 's' : ''}!
              </p>
              <p className="text-xs text-green-700">
                Next billing: <strong>${discountInfo.nextBillingAmount}</strong> (base plan only)
                {discountInfo.totalSaved > 0 && ` • Saved $${discountInfo.totalSaved} so far`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/profile?tab=billing')}
              className="text-sm font-medium text-green-700 hover:text-green-800 flex items-center gap-1 px-3 py-1 rounded-md hover:bg-green-100 transition-colors"
            >
              View Details
              <ChevronRight className="h-4 w-4" />
            </button>

            <button
              onClick={handleDismiss}
              className="text-green-600 hover:text-green-800 p-1 rounded-md hover:bg-green-100 transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
